import { useState } from "react";
import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { Switch } from "@/components/Switch";
import { Textarea } from "@/components/Textarea";
import { ProviderConflictList } from "@/pages/Providers/ProviderConflictList";
import {
    ProviderApiError,
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import type { Provider } from "@/pages/Providers/types";

interface ProviderConflictPanelProps {
    provider: Provider;
    onView: (id: number) => void;
    onSaved: (provider: Provider) => void;
}

export const ProviderConflictPanel = ({
    provider,
    onView,
    onSaved,
}: ProviderConflictPanelProps) => {
    const { t } = useProviderContext();
    const { request } = useProviderApi();
    const [observation, setObservation] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const warnings = provider.conflicts.filter(
        (conflict) => conflict.severity === "warning" && !conflict.acknowledged
    );
    const acknowledge = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            const response = await request<{ item: Provider }>(
                `/${provider.id}/conflicts/acknowledge`,
                {
                    confirmed,
                    observation,
                    version: provider.version,
                    fingerprints: warnings.map(
                        (conflict) => conflict.fingerprint
                    ),
                }
            );
            setObservation("");
            setConfirmed(false);
            onSaved(response.item);
        } catch (failure: unknown) {
            setError(
                failure instanceof ProviderApiError && failure.status === 409
                    ? "providers.errors.stale"
                    : "providers.errors.request"
            );
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="flex flex-col gap-4">
            <ProviderConflictList
                conflicts={provider.conflicts}
                onView={onView}
            />
            {warnings.length > 0 && (
                <div className="flex flex-col gap-4 rounded-xl border border-slate-200 p-4">
                    <p className="text-sm">
                        {t("providers.ui.acknowledge_explanation")}
                    </p>
                    {error && <Alert variant="error">{t(error)}</Alert>}
                    <Textarea
                        label={t("providers.ui.justification")}
                        value={observation}
                        onChange={(event) => setObservation(event.target.value)}
                        required
                        maxLength={5000}
                    />
                    <Switch
                        checked={confirmed}
                        onChange={setConfirmed}
                        label={t("providers.ui.acknowledge_confirm")}
                    />
                    <Button
                        className="self-start"
                        variant="primary"
                        disabled={
                            busy || !confirmed || observation.trim().length < 5
                        }
                        onClick={acknowledge}
                    >
                        {t("providers.ui.acknowledge")}
                    </Button>
                </div>
            )}
        </div>
    );
};
